/*
 *  Copyright (c) 2023 NOOGA DEVELOPMENT, All rights reserved.
 *  Author Rihard Tsybulka <rykhard.tsybulka@noogadev.com>
 */

import { DocumentDetailsAction, DocumentDetailsActionType } from '../actions/documentDetailsActions';
import { DocumentResource, ProductTable, UnknownDocumentContent } from '../api/documents/documentsClient.typings';
import { Cashback, VatVerificationInfo } from '../api/cashback/cashbackClient.typings';

export interface DocumentDetailsState {
    isLoading: boolean;
    isCategoriesLoading: boolean;
    categoriesLoadingError: boolean;
    documentDetails: DocumentResource;

    isLoadingCashbackInfo: boolean;
    isCashbackError: boolean;
    cashbackInfo: Cashback;

    isLoadingVatVerificationInfo: boolean;
    isVatVerificationInfoError: boolean;
    vatVerificationInfo: VatVerificationInfo;
}

const initialState: DocumentDetailsState = {
    isLoading: false,
    isCategoriesLoading: false,
    categoriesLoadingError: false,
    documentDetails: null,

    isLoadingCashbackInfo: false,
    isCashbackError: false,
    cashbackInfo: null,

    isLoadingVatVerificationInfo: false,
    isVatVerificationInfoError: false,
    vatVerificationInfo: null,
};

function documentDetailsReducer(
    state: DocumentDetailsState = initialState,
    action: DocumentDetailsAction,
): DocumentDetailsState {
    switch (action.type) {
        case DocumentDetailsActionType.OPEN_REGISTRY_DOCUMENT_CARD_PAGE:
            return {
                ...state,
                isLoading: true,
                isLoadingCashbackInfo: true,
                isLoadingVatVerificationInfo: true,
            };

        case DocumentDetailsActionType.FETCH_DOCUMENT_CARD_DETAILS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                documentDetails: action.payload,
            };

        case DocumentDetailsActionType.FETCH_DOCUMENT_CARD_DETAILS_ERROR:
            return {
                ...state,
                isLoading: false,
            };

        case DocumentDetailsActionType.FETCH_DOCUMENT_ITEMS_AI_CATEGORIES_START:
            return {
                ...state,
                isCategoriesLoading: true,
                categoriesLoadingError: false,
            };

        case DocumentDetailsActionType.FETCH_DOCUMENT_ITEMS_AI_CATEGORIES_SUCCESS: {
            const docContent: UnknownDocumentContent = { ...state.documentDetails.content };
            if (Object.keys(docContent).includes('items') && Array.isArray((docContent as ProductTable).items)) {
                const updatedItems = [...(docContent as ProductTable).items];
                updatedItems.forEach((item) => {
                    item.itemAiCategories = action.payload.find((categoryItem) => categoryItem.id === item.itemNumber);
                });
                (docContent as ProductTable).items = updatedItems;
            }
            return {
                ...state,
                documentDetails: { ...state.documentDetails, content: docContent },
                isCategoriesLoading: false,
                categoriesLoadingError: false,
            };
        }

        case DocumentDetailsActionType.FETCH_DOCUMENT_ITEMS_AI_CATEGORIES_ERROR:
            return {
                ...state,
                isCategoriesLoading: false,
                categoriesLoadingError: true,
            };

        case DocumentDetailsActionType.FETCH_CASHBACK_INFO_START:
            return {
                ...state,
                isLoadingCashbackInfo: true,
                isCashbackError: false,
            };

        case DocumentDetailsActionType.FETCH_CASHBACK_INFO_SUCCESS:
            return {
                ...state,
                cashbackInfo: action.payload,
                isLoadingCashbackInfo: false,
                isCashbackError: false,
            };

        case DocumentDetailsActionType.FETCH_CASHBACK_INFO_ERROR:
            return {
                ...state,
                isLoadingCashbackInfo: false,
                isCashbackError: true,
            };

        case DocumentDetailsActionType.FETCH_VAT_VERIFICATION_INFO_START:
            return {
                ...state,
                isLoadingVatVerificationInfo: true,
                isVatVerificationInfoError: false,
            };

        case DocumentDetailsActionType.FETCH_VAT_VERIFICATION_INFO_SUCCESS:
            return {
                ...state,
                vatVerificationInfo: action.payload,
                isLoadingVatVerificationInfo: false,
                isVatVerificationInfoError: false,
            };

        case DocumentDetailsActionType.FETCH_VAT_VERIFICATION_INFO_ERROR:
            return {
                ...state,
                isLoadingVatVerificationInfo: false,
                isVatVerificationInfoError: true,
            };

        case DocumentDetailsActionType.EXIT_REGISTRY_DOCUMENT_CARD_PAGE:
            return {
                ...state,
                isLoading: false,
                isCategoriesLoading: false,
                categoriesLoadingError: false,
                documentDetails: null,

                isCashbackError: false,
                isLoadingCashbackInfo: false,
                cashbackInfo: null,

                isVatVerificationInfoError: false,
                isLoadingVatVerificationInfo: false,
                vatVerificationInfo: null,
            };

        default:
            return state;
    }
}

export default documentDetailsReducer;
